/**
 * 信息录入模块功能测试脚本
 * 独立测试脚本，验证模块的各项功能
 */

import { 
  validateField, 
  validateForm, 
  isFormValid, 
  validateBucketCount,
  validateDateLogic,
  formatDate,
  getTodayString
} from '../../utils/validation';
import { CertificateInfo } from '../../types/certificate';

/**
 * 测试数据
 */
const validTestData: CertificateInfo = {
  orderNumber: 'ORD-2024-001',
  shipmentDate: '2024-01-15',
  batchNumber: 'BATCH001',
  bucketCount: 10,
  productionDate: '2024-01-10',
  testReportDate: '2024-01-12'
};

const invalidTestData: CertificateInfo = {
  orderNumber: '', // 空值
  shipmentDate: '2024-01-05', // 早于生产日期
  batchNumber: 'A', // 太短
  bucketCount: 0, // 无效桶数
  productionDate: '2024-01-10',
  testReportDate: '2024-01-01' // 早于生产日期
};

/**
 * 测试函数
 */
function runTests() {
  console.log('🧪 开始测试信息录入模块功能...\n');

  // 测试字段验证
  console.log('📝 测试字段验证功能:');
  
  // 测试订单号验证
  const orderNumberValid = validateField('orderNumber', 'ORD-2024-001');
  const orderNumberInvalid = validateField('orderNumber', '');
  console.log('✅ 订单号验证 - 有效:', orderNumberValid === '');
  console.log('❌ 订单号验证 - 无效:', orderNumberInvalid !== '');

  // 测试桶数验证
  const bucketCountValid = validateBucketCount(10);
  const bucketCountInvalid = validateBucketCount(0);
  console.log('✅ 桶数验证 - 有效:', bucketCountValid === '');
  console.log('❌ 桶数验证 - 无效:', bucketCountInvalid !== '');

  // 测试日期逻辑验证
  console.log('\n📅 测试日期逻辑验证:');
  const dateLogicValid = validateDateLogic(validTestData);
  const dateLogicInvalid = validateDateLogic(invalidTestData);
  console.log('✅ 日期逻辑 - 有效:', dateLogicValid.length === 0);
  console.log('❌ 日期逻辑 - 无效:', dateLogicInvalid.length > 0);
  if (dateLogicInvalid.length > 0) {
    console.log('   错误信息:', dateLogicInvalid);
  }

  // 测试整体表单验证
  console.log('\n🔍 测试整体表单验证:');
  const validFormErrors = validateForm(validTestData);
  const invalidFormErrors = validateForm(invalidTestData);
  console.log('✅ 有效表单验证:', Object.keys(validFormErrors).length === 0);
  console.log('❌ 无效表单验证:', Object.keys(invalidFormErrors).length > 0);
  if (Object.keys(invalidFormErrors).length > 0) {
    console.log('   错误详情:', invalidFormErrors);
  }

  // 测试表单有效性检查
  console.log('\n✔️ 测试表单有效性检查:');
  const isValidForm = isFormValid(validTestData);
  const isInvalidForm = isFormValid(invalidTestData);
  console.log('✅ 有效表单:', isValidForm === true);
  console.log('❌ 无效表单:', isInvalidForm === false);

  // 测试工具函数
  console.log('\n🛠️ 测试工具函数:');
  const today = getTodayString();
  const formattedDate = formatDate(new Date('2024-01-15'));
  console.log('✅ 获取今天日期:', today.match(/^\d{4}-\d{2}-\d{2}$/) !== null);
  console.log('✅ 日期格式化:', formattedDate === '2024-01-15');

  console.log('\n🎉 模块功能测试完成！');
}

/**
 * 验证类型定义
 */
function validateTypes() {
  console.log('\n📋 验证类型定义:');
  
  // 检查 CertificateInfo 类型的所有必需字段
  const requiredFields: (keyof CertificateInfo)[] = [
    'orderNumber',
    'shipmentDate', 
    'batchNumber',
    'bucketCount',
    'productionDate',
    'testReportDate'
  ];

  const hasAllFields = requiredFields.every(field => field in validTestData);
  console.log('✅ 合格证信息类型完整:', hasAllFields);

  console.log('✅ TypeScript 类型定义验证通过');
}

/**
 * 模拟表单提交测试
 */
function simulateFormSubmission() {
  console.log('\n📤 模拟表单提交测试:');
  
  try {
    // 模拟表单数据处理
    const processedData = {
      ...validTestData,
      // 确保日期格式正确
      shipmentDate: formatDate(validTestData.shipmentDate),
      productionDate: formatDate(validTestData.productionDate),
      testReportDate: formatDate(validTestData.testReportDate)
    };

    console.log('✅ 表单数据处理成功');
    console.log('📊 处理后的数据:', processedData);

    // 模拟验证通过
    const errors = validateForm(processedData);
    if (Object.keys(errors).length === 0) {
      console.log('✅ 表单验证通过，可以提交');
    } else {
      console.log('❌ 表单验证失败:', errors);
    }

  } catch (error) {
    console.error('❌ 表单处理失败:', error);
  }
}

// 如果直接运行此脚本
if (typeof window === 'undefined') {
  // Node.js 环境
  runTests();
  validateTypes();
  simulateFormSubmission();
}

// 导出测试函数供外部调用
export {
  runTests,
  validateTypes,
  simulateFormSubmission,
  validTestData,
  invalidTestData
};